import * as React from 'react';
import {
    Alert, Box, Chip, CircularProgress, Grid, Link as MuiLink, Stack, Tab, Table, TableBody, TableCell, TableHead, TableRow,
    Tabs, Tooltip, Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { AI_LABELS, getAiAgentStats } from '../../Api';
import { AiAgentStatsDto } from '../../models/dtos/AiDto';
import { aiLoadErrorText, formatAiCount, formatAiUsd } from './AiShared';

const PERIODS = [
    { label: 'Last 24 Hours', hours: 24 },
    { label: 'Last 7 Days', hours: 24 * 7 },
    { label: 'Last 30 Days', hours: 24 * 30 },
];

/** The tool an agent calls to hand a chat to a human (counted as escalations). */
const ESCALATION_TOOL = 'escalate_to_human';

function Metric({ value, label, hint, size = 'h3' }: { value: React.ReactNode; label: string; hint?: string; size?: 'h3' | 'h5' }) {
    const body = (
        <Box sx={{ textAlign: 'center' }}>
            <Typography variant={size} color="primary" sx={{ fontWeight: 'bold' }}>{value}</Typography>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
        </Box>
    );
    return hint ? <Tooltip title={hint}>{body}</Tooltip> : body;
}

/**
 * An AI hub agent's numbers for the last 24 h / 7 d / 30 d (GET /api/dashboard/ai/agents/{code}/stats): replies,
 * conversations, users, cost, tokens and its tool calls. Used on the Support page for Betty (support.betty).
 */
export default function AiAgentStatsPanel({ agentCode }: { agentCode: string }) {
    const [period, setPeriod] = React.useState(0);
    const [stats, setStats] = React.useState<AiAgentStatsDto | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
        let cancelled = false;
        setLoading(true);
        getAiAgentStats(agentCode, PERIODS[period].hours).then(result => {
            if (cancelled) return;
            setLoading(false);
            if (result == null) {
                setError(aiLoadErrorText(AI_LABELS.agentStats, `Failed to load the stats of ${agentCode}.`));
                setStats(null);
                return;
            }
            setError(null);
            setStats(result);
        });
        return () => { cancelled = true; };
    }, [agentCode, period]);

    const escalations = stats?.tools.find(t => t.name === ESCALATION_TOOL)?.calls ?? 0;
    const promptTokens = stats ? stats.inputTokens + stats.cacheReadTokens + stats.cacheWriteTokens : 0;
    const cacheHit = stats && promptTokens > 0 ? Math.round((stats.cacheReadTokens / promptTokens) * 100) : null;

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" gap={1}>
                <Stack direction="row" gap={1} alignItems="center">
                    <MuiLink component={RouterLink} to={`/ai/agents/${encodeURIComponent(agentCode)}`} underline="hover" sx={{ fontFamily: 'monospace' }}>
                        {agentCode}
                    </MuiLink>
                    {stats?.modelKey && <Chip size="small" variant="outlined" color="primary" label={`Model: ${stats.modelKey}`} />}
                </Stack>
                <Stack direction="row" gap={2}>
                    <MuiLink component={RouterLink} to="/ai/conversations" underline="hover" variant="body2">Conversations</MuiLink>
                    <MuiLink component={RouterLink} to="/ai/runs" underline="hover" variant="body2">Runs</MuiLink>
                </Stack>
            </Stack>
            <Tabs value={period} onChange={(_, value: number) => setPeriod(value)} sx={{ mb: 2 }} variant="fullWidth">
                {PERIODS.map(p => <Tab key={p.hours} label={p.label} />)}
            </Tabs>
            <Box sx={{ bgcolor: 'white', borderRadius: 1, p: 2 }}>
                {error && <Alert severity="error">{error}</Alert>}
                {loading && stats == null && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={32} /></Box>}
                {stats && (
                    <Box sx={{ opacity: loading ? 0.5 : 1 }}>
                        <Grid container spacing={3} sx={{ mb: 3 }}>
                            <Grid item xs={6} sm={3}>
                                <Metric value={formatAiCount(stats.succeeded)} label="Replies"
                                    hint={`${formatAiCount(stats.runs)} runs: ${formatAiCount(stats.succeeded)} answered, ${formatAiCount(stats.failed)} failed${stats.inProgress ? `, ${stats.inProgress} running` : ''}`} />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Metric value={formatAiCount(stats.conversations)} label="Conversations"
                                    hint={`Unique chats Betty answered in; ${formatAiCount(stats.newConversations)} started in this period`} />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Metric value={formatAiCount(stats.endUsers)} label="Distinct Users" />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Metric value={formatAiUsd(stats.costUsd)} label="Cost"
                                    hint="Every model call of her runs, incl. conversation summaries" />
                            </Grid>
                        </Grid>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={6} sm={3}>
                                <Metric size="h5" value={formatAiCount(stats.inputTokens + stats.outputTokens + stats.cacheReadTokens + stats.cacheWriteTokens)}
                                    label="Total Tokens"
                                    hint={`${formatAiCount(stats.inputTokens)} input · ${formatAiCount(stats.outputTokens)} output · ${formatAiCount(stats.cacheReadTokens)} cache read · ${formatAiCount(stats.cacheWriteTokens)} cache write`} />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Metric size="h5" value={cacheHit == null ? '—' : `${cacheHit}%`} label="Prompt Cache Hits"
                                    hint="Share of prompt tokens read from cache (billed at a fraction of the input price)" />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Metric size="h5" value={formatAiCount(stats.toolCalls)} label="Tool Invocations"
                                    hint={`${formatAiCount(stats.toolErrors)} refused / failed · ${stats.avgTurns.toFixed(1)} model turns per run on average`} />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Metric size="h5" value={formatAiCount(escalations)} label="Escalations"
                                    hint="Chats Betty handed to a human with a summary (escalate_to_human)" />
                            </Grid>
                        </Grid>
                        {stats.tools.length > 0 && (
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Tool</TableCell>
                                        <TableCell align="right">Calls</TableCell>
                                        <TableCell align="right">Refused / failed</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {stats.tools.map(tool => (
                                        <TableRow key={tool.name}>
                                            <TableCell sx={{ fontFamily: 'monospace' }}>{tool.name}</TableCell>
                                            <TableCell align="right">{formatAiCount(tool.calls)}</TableCell>
                                            <TableCell align="right">{tool.errors > 0 ? formatAiCount(tool.errors) : '—'}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                        <MuiLink component={RouterLink} to={`/ai/tools?agentCode=${encodeURIComponent(agentCode)}`} variant="body2" sx={{ display: 'inline-block', mt: 1 }}>
                            Tool call details, failure rates and error messages
                        </MuiLink>
                        {(stats.failed > 0 || stats.blockedCalls > 0) && (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                                {stats.failed > 0 ? `${formatAiCount(stats.failed)} failed runs (the chat went to a human). ` : ''}
                                {stats.blockedCalls > 0 ? `${formatAiCount(stats.blockedCalls)} calls blocked by a budget.` : ''}
                            </Typography>
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    );
}
